// src/pages/sqf/SqfAiImportPage.js
import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import sqfAiService from '../../services/sqfAiService';
import { useLanguage, t } from '../../context/LanguageContext';
import SqfNav from '../../components/SqfNav';

const ACCEPTED_TYPES = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword', 'image/jpeg', 'image/jpg', 'image/png'];
const ACCEPTED_EXT = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
const MAX_MB = 15;

const CONFIDENCE_BADGE = {
  high:   { cls: 'bg-success', label: 'High',   labelEs: 'Alta' },
  medium: { cls: 'bg-warning text-dark', label: 'Medium', labelEs: 'Media' },
  low:    { cls: 'bg-danger',  label: 'Low',    labelEs: 'Baja' },
};

export default function SqfAiImportPage() {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Step 1: file
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [dragging, setDragging] = useState(false);

  // Step 2: configure
  const [importType, setImportType] = useState('checklist'); // 'checklist' | 'policy'
  const [hint, setHint] = useState('');

  // Step 3: result / progress
  const [step, setStep] = useState(1); // 1 | 2 | 3
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // ─── File handling ──────────────────────────────────────────────────────────

  function validateFile(f) {
    if (!f) return t('No file selected.', 'Ningún archivo seleccionado.', lang);
    if (!ACCEPTED_TYPES.includes(f.type) && !f.name.match(/\.(pdf|doc|docx|jpg|jpeg|png)$/i)) {
      return t('Unsupported file type. Use PDF, Word, or image.', 'Tipo de archivo no compatible. Use PDF, Word o imagen.', lang);
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      return t(`File too large. Maximum is ${MAX_MB} MB.`, `Archivo demasiado grande. Máximo ${MAX_MB} MB.`, lang);
    }
    return '';
  }

  function pickFile(f) {
    const err = validateFile(f);
    if (err) { setFileError(err); setFile(null); return; }
    setFileError('');
    setFile(f);
  }

  function onInputChange(e) { if (e.target.files[0]) pickFile(e.target.files[0]); }
  function onDrop(e) {
    e.preventDefault(); setDragging(false);
    if (e.dataTransfer.files[0]) pickFile(e.dataTransfer.files[0]);
  }

  // ─── Import flow ────────────────────────────────────────────────────────────

  async function runImport() {
    if (!file) return;
    setError('');
    setUploading(true);

    try {
      // Upload to S3
      const { fileUrl, mimeType } = await sqfAiService.uploadFileForAi(file);
      setUploading(false);
      setProcessing(true);

      // Call appropriate AI endpoint
      let res;
      if (importType === 'checklist') {
        res = await sqfAiService.importChecklist({ fileUrl, mimeType, hint });
      } else {
        res = await sqfAiService.importPolicy({ fileUrl, mimeType, hint });
      }

      setResult({ ...res, importType });
      setStep(3);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t('Import failed. Please try again.', 'Importación fallida. Intente nuevamente.', lang);
      setError(msg);
    } finally {
      setUploading(false);
      setProcessing(false);
    }
  }

  function handleContinue() {
    if (!result) return;
    if (result.importType === 'checklist') {
      navigate(`/sqf/templates/${result.templateId}/edit`);
    } else {
      navigate(`/sqf/policies/${result.policyId}/edit`);
    }
  }

  // ─── Render helpers ─────────────────────────────────────────────────────────

  const stepLabel = (n) => (
    <div className={`d-flex align-items-center justify-content-center rounded-circle border ${step >= n ? 'bg-primary border-primary text-white' : 'border-secondary text-muted'}`}
      style={{ width: 28, height: 28, fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
      {step > n ? '✓' : n}
    </div>
  );

  const isLoading = uploading || processing;

  return (
    <>
      <SqfNav />
      <div className="container py-4" style={{ maxWidth: 640 }}>
      {/* Header */}
      <div className="d-flex align-items-center gap-2 mb-4">
        <Link to="/sqf" className="text-muted text-decoration-none" style={{ fontSize: 13 }}>
          ← {t('SQF', 'SQF', lang)}
        </Link>
        <span className="text-muted">/</span>
        <h5 className="mb-0">{t('AI Document Import', 'Importación de Documento con IA', lang)}</h5>
      </div>

      {/* Step indicator */}
      <div className="d-flex align-items-center gap-2 mb-4">
        {stepLabel(1)}
        <span className={`flex-fill border-top ${step > 1 ? 'border-primary' : 'border-secondary'}`} style={{ borderWidth: 2 }} />
        {stepLabel(2)}
        <span className={`flex-fill border-top ${step > 2 ? 'border-primary' : 'border-secondary'}`} style={{ borderWidth: 2 }} />
        {stepLabel(3)}
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-start gap-2 py-2" style={{ fontSize: 13 }}>
          <span>⚠️</span>
          <span className="flex-fill">{error}</span>
          <button className="btn-close btn-sm" onClick={() => setError('')} />
        </div>
      )}

      {/* ── Step 1: Upload ─────────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="card shadow-sm">
          <div className="card-body p-4">
            <h6 className="fw-bold mb-1">{t('Step 1 — Upload Document', 'Paso 1 — Subir Documento', lang)}</h6>
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
              {t('Upload a PDF, Word file, or image of your existing checklist or policy.', 'Sube un PDF, archivo Word o imagen de tu lista de verificación o política existente.', lang)}
            </p>

            {/* Drop zone */}
            <div
              className={`border rounded-3 p-4 text-center ${dragging ? 'border-primary bg-primary bg-opacity-10' : 'border-dashed'}`}
              style={{ cursor: 'pointer', borderStyle: 'dashed', minHeight: 140 }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
            >
              {file ? (
                <div>
                  <div style={{ fontSize: 32 }}>📄</div>
                  <div className="fw-semibold mt-1">{file.name}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                  <button
                    className="btn btn-link btn-sm text-danger p-0 mt-1"
                    onClick={e => { e.stopPropagation(); setFile(null); }}
                  >
                    {t('Remove', 'Eliminar', lang)}
                  </button>
                </div>
              ) : (
                <div className="text-muted">
                  <div style={{ fontSize: 32 }}>📁</div>
                  <div className="mt-1" style={{ fontSize: 13 }}>
                    {t('Drag & drop or click to browse', 'Arrastra aquí o haz clic para seleccionar', lang)}
                  </div>
                  <div style={{ fontSize: 11 }}>PDF, Word, JPG, PNG · max {MAX_MB} MB</div>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept={ACCEPTED_EXT} className="d-none" onChange={onInputChange} />

            {fileError && <div className="text-danger mt-2" style={{ fontSize: 12 }}>{fileError}</div>}

            <div className="d-flex justify-content-end mt-3">
              <button className="btn btn-primary" disabled={!file} onClick={() => setStep(2)}>
                {t('Next →', 'Siguiente →', lang)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 2: Configure ──────────────────────────────────────────────── */}
      {step === 2 && (
        <div className="card shadow-sm">
          <div className="card-body p-4">
            <h6 className="fw-bold mb-1">{t('Step 2 — Configure', 'Paso 2 — Configurar', lang)}</h6>
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
              {t('Tell the AI what type of document this is to improve accuracy.', 'Indica a la IA qué tipo de documento es para mejorar la precisión.', lang)}
            </p>

            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                {t('Document Type', 'Tipo de Documento', lang)}
              </label>
              <div className="d-flex gap-2">
                {[
                  { value: 'checklist', label: t('Checklist / Form', 'Lista de Verificación / Formulario', lang), icon: '☑️' },
                  { value: 'policy',    label: t('Policy / Procedure', 'Política / Procedimiento', lang),      icon: '📋' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    className={`btn flex-fill py-3 ${importType === opt.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setImportType(opt.value)}
                  >
                    <div style={{ fontSize: 20 }}>{opt.icon}</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>{opt.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                {t('Hint (optional)', 'Descripción breve (opcional)', lang)}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder={t('e.g. "Pre-op sanitation SSOP" or "Metal detector log"', 'p.ej. "SSOP saneamiento pre-operación" o "Registro detector de metales"', lang)}
                value={hint}
                onChange={e => setHint(e.target.value)}
              />
              <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                {t('A short description improves AI accuracy, especially for scanned documents.', 'Una descripción breve mejora la precisión de la IA, especialmente para documentos escaneados.', lang)}
              </div>
            </div>

            {/* File summary */}
            <div className="alert alert-light py-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
              <span>📄</span>
              <span className="text-truncate">{file?.name}</span>
              <span className="ms-auto text-muted">{file ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : ''}</span>
            </div>

            <div className="d-flex justify-content-between mt-3">
              <button className="btn btn-outline-secondary" onClick={() => setStep(1)}>
                ← {t('Back', 'Atrás', lang)}
              </button>
              <button className="btn btn-primary d-flex align-items-center gap-2" onClick={runImport} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" />
                    {uploading
                      ? t('Uploading…', 'Subiendo…', lang)
                      : t('AI is reading the document…', 'La IA está leyendo el documento…', lang)}
                  </>
                ) : (
                  t('Import with AI ✨', 'Importar con IA ✨', lang)
                )}
              </button>
            </div>

            {processing && (
              <div className="alert alert-info mt-3 py-2" style={{ fontSize: 12 }}>
                {t(
                  'Claude is extracting the document structure. This usually takes 10–30 seconds.',
                  'Claude está extrayendo la estructura del documento. Esto suele tardar entre 10 y 30 segundos.',
                  lang
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Step 3: Review ─────────────────────────────────────────────────── */}
      {step === 3 && result && (
        <div className="card shadow-sm">
          <div className="card-body p-4">
            <div className="text-center mb-3">
              <div style={{ fontSize: 48 }}>✅</div>
              <h5 className="mt-2 mb-0">{t('Import Complete', 'Importación Completa', lang)}</h5>
              <p className="text-muted mt-1" style={{ fontSize: 13 }}>
                {t('A draft has been created. Review it in the editor before publishing.', 'Se creó un borrador. Revísalo en el editor antes de publicar.', lang)}
              </p>
            </div>

            <div className="border rounded-3 p-3 mb-3 bg-light">
              <div className="row g-2" style={{ fontSize: 13 }}>
                <div className="col-12">
                  <span className="text-muted">{t('Name', 'Nombre', lang)}: </span>
                  <strong>{result.templateName || result.policyName}</strong>
                </div>
                {result.fieldCount !== undefined && (
                  <div className="col-6">
                    <span className="text-muted">{t('Fields detected', 'Campos detectados', lang)}: </span>
                    <strong>{result.fieldCount}</strong>
                  </div>
                )}
                {result.sqfChapterRef && (
                  <div className="col-6">
                    <span className="text-muted">{t('SQF Clause', 'Cláusula SQF', lang)}: </span>
                    <strong>{result.sqfChapterRef}</strong>
                  </div>
                )}
                {result.confidence && (
                  <div className="col-6">
                    <span className="text-muted">{t('AI Confidence', 'Confianza IA', lang)}: </span>
                    <span className={`badge ${CONFIDENCE_BADGE[result.confidence]?.cls || 'bg-secondary'}`}>
                      {t(
                        CONFIDENCE_BADGE[result.confidence]?.label,
                        CONFIDENCE_BADGE[result.confidence]?.labelEs,
                        lang
                      )}
                    </span>
                  </div>
                )}
                {result.category && (
                  <div className="col-6">
                    <span className="text-muted">{t('Category', 'Categoría', lang)}: </span>
                    <strong>{result.category}</strong>
                  </div>
                )}
              </div>
            </div>

            {result.confidence === 'low' && (
              <div className="alert alert-warning py-2 mb-3" style={{ fontSize: 12 }}>
                ⚠️ {t(
                  'Low confidence — the document may have been a low-quality scan. Review all fields carefully before publishing.',
                  'Confianza baja — el documento puede ser un escaneo de baja calidad. Revisa todos los campos cuidadosamente antes de publicar.',
                  lang
                )}
              </div>
            )}

            <div className="alert alert-info py-2 mb-3" style={{ fontSize: 12 }}>
              🔒 {t(
                'The draft is unpublished. You must review and publish it manually.',
                'El borrador no está publicado. Debes revisarlo y publicarlo manualmente.',
                lang
              )}
            </div>

            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary flex-fill" onClick={() => { setStep(1); setFile(null); setResult(null); setHint(''); }}>
                {t('Import Another', 'Importar Otro', lang)}
              </button>
              <button className="btn btn-primary flex-fill" onClick={handleContinue}>
                {t('Open in Editor →', 'Abrir en Editor →', lang)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help box */}
      {step < 3 && (
        <div className="mt-4 text-muted" style={{ fontSize: 12 }}>
          <strong>{t('Supported formats:', 'Formatos admitidos:', lang)}</strong> PDF, Word (.docx), JPG, PNG
          <br />
          <strong>{t('Best results:', 'Mejores resultados:', lang)}</strong> {t(
            'Searchable (text-based) PDFs work best. Scanned images work but may have lower accuracy.',
            'Los PDFs con texto seleccionable funcionan mejor. Las imágenes escaneadas funcionan pero pueden tener menor precisión.',
            lang
          )}
        </div>
      )}
    </div>
    </>
  );
}
